import { useEffect, useState } from "react";
import {
  Archive,
  CheckCircle2,
  Database,
  HardDrive,
  RefreshCcw,
  Server,
  ShieldCheck
} from "lucide-react";
import type { ArchiveSettings } from "../types";
import { api } from "../lib/api";
import { formatDateTime } from "../lib/format";
import { Badge, Button, Loading, Switch } from "../components/ui";

export default function SettingsPage({
  notify
}: {
  notify: (tone: "success" | "error" | "info", title: string, message?: string) => void;
}) {
  const [settings, setSettings] = useState<ArchiveSettings | null>(null);
  const [health, setHealth] = useState<{
    api: string;
    database: string;
    connectedAccounts: number;
    attentionAccounts: number;
    lastCheckedAt: string;
  } | null>(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [archive, system] = await Promise.all([api.archiveSettings(), api.systemHealth()]);
      setSettings(archive);
      setHealth(system);
    } catch (error) {
      notify("error", "Couldn’t load settings", (error as Error).message);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (!settings || !health) return <Loading label="Checking system configuration…" />;

  const save = async () => {
    setSaving(true);
    try {
      await api.updateArchiveSettings(settings);
      notify("success", "Archive policy saved");
    } catch (error) {
      notify("error", "Couldn’t save archive policy", (error as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page settings-page">
      <div className="page-heading">
        <div>
          <span className="eyebrow">Administration</span>
          <h1>System settings</h1>
          <p>Keep storage predictable and see the health of the local application services.</p>
        </div>
      </div>

      <div className="settings-grid">
        <section className="panel settings-card archive-card">
          <header>
            <span className="settings-icon violet">
              <Archive size={20} />
            </span>
            <div>
              <h2>Automatic archiving</h2>
              <p>Move older message bodies out of the active working set.</p>
            </div>
            <Switch
              checked={settings.archiveEnabled}
              onChange={(archiveEnabled) => setSettings({ ...settings, archiveEnabled })}
              label="Automatic archiving"
            />
          </header>
          <div className="settings-body">
            <label className="field">
              <span>Archive messages after</span>
              <select
                value={settings.archiveAfterDays}
                disabled={!settings.archiveEnabled}
                onChange={(event) =>
                  setSettings({ ...settings, archiveAfterDays: Number(event.target.value) })
                }
              >
                <option value={90}>90 days</option>
                <option value={180}>180 days</option>
                <option value={365}>1 year</option>
                <option value={1095}>3 years</option>
              </select>
            </label>
            <div className="switch-row">
              <div>
                <strong>Archive attachments</strong>
                <span>Move file payloads while searchable metadata remains available.</span>
              </div>
              <Switch
                checked={settings.archiveAttachments}
                onChange={(archiveAttachments) =>
                  setSettings({ ...settings, archiveAttachments })
                }
                label="Archive attachments"
              />
            </div>
          </div>
          <footer>
            <Button onClick={save} disabled={saving}>
              {saving ? "Saving…" : "Save archive policy"}
            </Button>
          </footer>
        </section>

        <section className="panel settings-card health-card">
          <header>
            <span className="settings-icon teal">
              <Server size={20} />
            </span>
            <div>
              <h2>System health</h2>
              <p>Local API, database, and provider readiness.</p>
            </div>
            <Button tone="ghost" icon={<RefreshCcw size={15} />} onClick={load}>
              Check now
            </Button>
          </header>
          <div className="health-list">
            <div>
              <span className="health-icon">
                <Server size={17} />
              </span>
              <span>
                <strong>ASP.NET Core API</strong>
                <small>Local encrypted service boundary</small>
              </span>
              <Badge tone="success">
                <CheckCircle2 size={12} /> {health.api}
              </Badge>
            </div>
            <div>
              <span className="health-icon">
                <Database size={17} />
              </span>
              <span>
                <strong>SQLite database</strong>
                <small>Tenant-scoped application data</small>
              </span>
              <Badge tone="success">
                <CheckCircle2 size={12} /> {health.database}
              </Badge>
            </div>
            <div>
              <span className="health-icon">
                <HardDrive size={17} />
              </span>
              <span>
                <strong>Mail providers</strong>
                <small>
                  {health.connectedAccounts} connected · {health.attentionAccounts} need attention
                </small>
              </span>
              <Badge tone={health.attentionAccounts ? "warning" : "success"}>
                {health.attentionAccounts ? "Review" : "Ready"}
              </Badge>
            </div>
          </div>
          <footer className="health-foot">
            <ShieldCheck size={15} />
            Credentials are protected by ASP.NET Data Protection.
            <span>Checked {formatDateTime(health.lastCheckedAt)}</span>
          </footer>
        </section>
      </div>
    </div>
  );
}
